import React from 'react'

import useKeyPress from './use-key-press'

/**
 * Keeps track of stacked dialogs and popovers in top layer in order to know which one is currently the topmost.
 */
export class TopLayerRegistry {
  static instance: TopLayerRegistry | null = null

  private nextId: number = 0

  private topLayerStack: number[] = []

  constructor() {
    if (!TopLayerRegistry.instance) {
      TopLayerRegistry.instance = this
    }
  }

  static destroy() {
    TopLayerRegistry.instance = null
  }

  static mount(): number {
    let instance = TopLayerRegistry.instance
    if (!instance) {
      instance = new TopLayerRegistry()
    }

    const id = instance.nextId

    instance.nextId += 1
    instance.topLayerStack.push(id)

    return id
  }

  static unmount(id: number) {
    let instance = TopLayerRegistry.instance
    if (!instance) {
      instance = new TopLayerRegistry()
    }

    instance.topLayerStack = instance.topLayerStack.filter(stackId => stackId !== id)
  }

  static isTopmost(id: number): boolean {
    let instance = TopLayerRegistry.instance
    if (!instance) {
      instance = new TopLayerRegistry()
    }

    return instance.topLayerStack[instance.topLayerStack.length - 1] === id
  }
}

const useStackedCloseOnEscape = (isOpen: boolean, onClose: () => void, enabled = true) => {
  const idRef = React.useRef<number>(-1)

  useKeyPress(
    () => {
      if (TopLayerRegistry.isTopmost(idRef.current)) {
        onClose()
      }
    },
    'Escape',
    isOpen && enabled
  )

  // Register element in top layer registry.
  // Event dialogs and popovers which are not closable (enabled = false) should be registered to block the escape key for all others below.
  React.useEffect(() => {
    if (isOpen) {
      const id = TopLayerRegistry.mount()
      idRef.current = id

      return () => {
        TopLayerRegistry.unmount(id)
      }
    }

    return undefined
  }, [isOpen])
}

export default useStackedCloseOnEscape
